import { useEffect, useState } from 'react';

/**
 * Whether the data rail has to float over the transcript instead of sitting
 * beside it.
 *
 * Below this width there is no room for a 760px reading column next to a 420px
 * rail, so the same component becomes a modal panel — the breakpoint is written
 * once here and once in `.chat-stage`'s media query, which collapses the track
 * it would have occupied.
 *
 * The query lives with the rail's owner rather than inside the rail: one
 * listener for the page, and a component that can be rendered in either
 * position by a test passing a prop instead of mocking `matchMedia`.
 */
export const RAIL_OVERLAY_QUERY = '(max-width: 1199px)';

export const useRailOverlay = () => {
  const [isOverlay, setIsOverlay] = useState(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia(RAIL_OVERLAY_QUERY).matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const query = window.matchMedia(RAIL_OVERLAY_QUERY);
    const onChange = (event) => setIsOverlay(event.matches);
    // `addListener` for Safari before 14, which is still in the field on iPads
    // and would otherwise leave the rail beside a column that has no room.
    if (query.addEventListener) query.addEventListener('change', onChange);
    else query.addListener(onChange);
    setIsOverlay(query.matches);
    return () => {
      if (query.removeEventListener) query.removeEventListener('change', onChange);
      else query.removeListener(onChange);
    };
  }, []);

  return isOverlay;
};
